import React, { useState, useEffect } from 'react';
import soundController from './SoundController';

const SoundTest = () => {
  const [isReady, setIsReady] = useState(false);
  const [canPlayAudio, setCanPlayAudio] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [soundsByCategory, setSoundsByCategory] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    const initializeSounds = async () => {
      try {
        await soundController.initialize();
        setIsReady(soundController.isReady());
        setCanPlayAudio(soundController.canPlayAudio());

        if (soundController.config && soundController.config.categories) {
          setSoundsByCategory(soundController.config.categories);
        }

        // Check audio unlock status periodically for iOS
        const checkAudioStatus = () => {
          setCanPlayAudio(soundController.canPlayAudio());
        };
        const interval = setInterval(checkAudioStatus, 1000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error('Failed to initialize sound controller:', error);
      }
    };

    initializeSounds();
  }, []);

  const playSound = async (eventName) => {
    setCurrentlyPlaying(eventName);
    setTestResults(prev => ({ ...prev, [eventName]: 'playing' }));

    try {
      await soundController.play(eventName);
      setTestResults(prev => ({ ...prev, [eventName]: 'success' }));
    } catch (error) {
      console.error(`Failed to play sound ${eventName}:`, error);
      setTestResults(prev => ({ ...prev, [eventName]: 'error' }));
    }

    setTimeout(() => {
      setCurrentlyPlaying(null);
    }, 2000);
  };

  const playAllSounds = async () => {
    const allEvents = soundController.getAvailableEvents();

    for (const eventName of allEvents) {
      await playSound(eventName);
      // Wait 3 seconds between sounds
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  };

  const getStatusIcon = (eventName) => {
    const status = testResults[eventName];
    switch (status) {
      case 'playing': return '🔊';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '⏸️';
    }
  };

  const getStatusColor = (eventName) => {
    const status = testResults[eventName];
    switch (status) {
      case 'playing': return 'text-blue-400';
      case 'success': return 'text-green-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (!isReady) {
    return (
      <div className="p-6 bg-gray-900 text-white">
        <h2 className="text-2xl font-bold mb-4">Sound System Test</h2>
        <p className="text-yellow-400">Initializing sound controller...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-6">Sound System Test</h2>

        {/* iOS Audio Status */}
        {soundController.isIOS() && (
          <div className={`mb-6 p-4 rounded-lg border ${canPlayAudio ? 'bg-green-900/50 border-green-700' : 'bg-yellow-900/50 border-yellow-700'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{canPlayAudio ? '🔊' : '🔇'}</span>
                <div>
                  <h3 className="font-semibold">
                    iOS Audio Status: {canPlayAudio ? 'Ready' : 'Waiting for user interaction'}
                  </h3>
                  {!canPlayAudio && (
                    <p className="text-sm text-gray-300 mt-1">
                      iOS requires user interaction to enable audio playback
                    </p>
                  )}
                </div>
              </div>
              {!canPlayAudio && (
                <button
                  onClick={async () => {
                    try {
                      await soundController.unlockAudio();
                      setCanPlayAudio(soundController.canPlayAudio());
                    } catch (error) {
                      console.error('Failed to unlock audio:', error);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Enable Audio
                </button>
              )}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="mb-6 p-4 bg-gray-800 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Controls</h3>
              <p className="text-gray-400 text-sm">Test individual sounds or play all sequentially</p>
            </div>
            <button
              onClick={playAllSounds}
              disabled={currentlyPlaying || !canPlayAudio}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              {currentlyPlaying ? 'Playing...' : 'Play All Sounds'}
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm">Volume:</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={soundController.getVolume()}
                onChange={(e) => soundController.setVolume(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-sm w-8">{Math.round(soundController.getVolume() * 100)}%</span>
            </div>

            <button
              onClick={() => soundController.toggleMuted()}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                soundController.isMuted()
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
            >
              {soundController.isMuted() ? '🔇 Unmute' : '🔊 Mute'}
            </button>
          </div>
        </div>

        {/* Sound Categories */}
        {Object.entries(soundsByCategory).map(([categoryName, categoryData]) => (
          <div key={categoryName} className="mb-8">
            <div className="mb-4">
              <h3 className="text-xl font-semibold capitalize mb-2">{categoryName} Sounds</h3>
              <p className="text-gray-400 text-sm">{categoryData.description}</p>
              <p className="text-gray-500 text-xs">Default Volume: {categoryData.defaultVolume}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryData.events.map((eventName) => (
                <div
                  key={eventName}
                  className="p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{eventName}</h4>
                    <span className={`text-lg ${getStatusColor(eventName)}`}>
                      {getStatusIcon(eventName)}
                    </span>
                  </div>

                  <div className="text-xs text-gray-400 mb-3">
                    {soundController.config.events[eventName]}
                  </div>

                  <button
                    onClick={() => playSound(eventName)}
                    disabled={currentlyPlaying === eventName || !canPlayAudio}
                    className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
                  >
                    {currentlyPlaying === eventName ? 'Playing...' : 'Play Sound'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* All Events (Fallback) */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">All Available Events</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {soundController.getAvailableEvents().map((eventName) => (
              <button
                key={eventName}
                onClick={() => playSound(eventName)}
                disabled={currentlyPlaying === eventName || !canPlayAudio}
                className={`p-2 rounded text-sm transition-colors ${
                  currentlyPlaying === eventName
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                } ${getStatusColor(eventName)}`}
              >
                {getStatusIcon(eventName)} {eventName}
              </button>
            ))}
          </div>
        </div>

        {/* Sound Files Status */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Sound Files Status</h3>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Required Sound Files:</h4>
                <div className="space-y-1 text-sm">
                  {Object.entries(soundController.config.events || {}).map(([event, path]) => (
                    <div key={event} className="flex justify-between">
                      <span className="text-gray-300">{event}:</span>
                      <span className="text-gray-500 font-mono text-xs">{path.split('/').pop()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Test Results:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✅</span>
                    <span>Successful: {Object.values(testResults).filter(r => r === 'success').length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-red-400">❌</span>
                    <span>Failed: {Object.values(testResults).filter(r => r === 'error').length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400">⏸️</span>
                    <span>Not tested: {soundController.getAvailableEvents().length - Object.keys(testResults).length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-900/50 border border-blue-700 rounded-lg p-4">
          <h4 className="font-medium mb-2">📋 Instructions</h4>
          <ul className="text-sm space-y-1 text-gray-300">
            <li>• Click individual "Play Sound" buttons to test specific sounds</li>
            <li>• Use "Play All Sounds" to test all sounds sequentially (3-second intervals)</li>
            <li>• Adjust volume and mute settings to test audio controls</li>
            <li>• Green checkmarks (✅) indicate successful playback</li>
            <li>• Red X marks (❌) indicate failed playback (likely missing audio files)</li>
            <li>• Add your actual .mp3 files to the paths shown in "Sound Files Status"</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SoundTest;